import * as React from "react";
import * as Storage from "aws-amplify/storage";
import * as moment from "moment-timezone";
import {
  useDropzone,
  FileRejection
} from "react-dropzone";
import StyledModal from "styled-react-modal";
import * as yup from "yup";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";
import _ from "lodash";
import { useHistory } from "react-router-dom";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Button } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import LoadingSpinner from "src/library/components/LoadingSpinner/LoadingSpinner";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import {
  ImportFileParams,
  useProcessJobsApi
} from "src/core/process-job/api/processJobs.api";

import { setLocalTerminology } from "./utils";
import { IStore } from "../store";

interface IProps {
  onDismiss: () => void;
  schema?: yup.Schema<any>;
  type: ImportFileParams["type"];
  terminology: ITerminology;
  open: boolean;
}

export const ImportModal: React.ReactFCWithChildren<IProps> = props => {
  const history = useHistory();
  const [ uploadingFile, setUploadingFile ] = React.useState<boolean | null>(null);
  const [ importing, setImporting ] = React.useState<boolean | null>(null);
  const [ fileErrors, setFileErrors ] = React.useState<string[] | null>(null);
  const [ key, setKey ] = React.useState<string | null>(null);
  const [ createImportJobResponse, createImportJobRequest ] = useProcessJobsApi("IMPORT_FILE");
  const { importSettings } = useMappedState(({ global }: IStore) => ({ importSettings: global.settings?.importSettings }));

  const localTerminology = React.useMemo(() => {
    return setLocalTerminology(props.type, props.terminology);
  }, [ props.terminology, props.type ]);

  React.useEffect(() => {
    if (createImportJobResponse.data) {
      setImporting(false);
    }

    if (createImportJobResponse.error) {
      setImporting(false);
      setFileErrors([ createImportJobResponse.error.message ]);
    }
  }, [ createImportJobResponse ]);

  React.useEffect(() => {
    if (createImportJobResponse.data?.id) {
      history.push(`/platform/process-job-queue/${createImportJobResponse.data.id}`);
    }
  }, [ createImportJobResponse.data, history ]);

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    const reader = new FileReader();

    // extend FileReader for IE 11 :roll_eyes:
    if (!FileReader.prototype.readAsBinaryString) {
      FileReader.prototype.readAsBinaryString = function (fileData: Blob) {
        let binary = "";
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const pt = this;
        const reader = new FileReader();

        reader.onload = () => {
          if (reader.result) {
            // @ts-ignore
            const bytes = new Uint8Array(reader.result);
            const length = bytes.byteLength;

            for (let i = 0; i < length; i++) {
              binary += String.fromCharCode(bytes[ i ]);
            }

            //pt.result  - readonly so assign binary
            // @ts-ignore
            pt.content = binary;

            if (pt.onload) {
              // @ts-ignore
              pt.onload();
            }
          }
        };
        reader.readAsText(fileData, "utf-8");
      };
    }

    setUploadingFile(true);

    reader.onabort = () => {
      setFileErrors([ "Error parsing the file. Aborted." ]);
    };

    reader.onerror = error => {
      setFileErrors([ `Error parsing the file ${error}` ]);
    };

    reader.onload = async e => {
      // @ts-ignore
      const binaryStr = !e ? reader.content : (e.target.result as string);

      try {
        const result = await Storage.uploadData({
          path: `public/imports/${props.type}/${moment.utc().format("DD-MM-YYYY:HH:mm:ss")}.csv`,
          data: binaryStr,
          options: {
            contentType: "text/csv",
            contentEncoding: "utf-8"
          }
        }).result;

        setUploadingFile(false);
        setKey(result.path);
      } catch (error) {
        console.error(error);
        setUploadingFile(null);
        setFileErrors([ "Could not upload file via ImportModel" ]);
      }
    };
    acceptedFiles.forEach((file: any) => reader.readAsText(file, "utf-8"));
  }, []);

  const onDropRejected = React.useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections && fileRejections[ 0 ] && fileRejections[ 0 ].errors) {
      setFileErrors(fileRejections[ 0 ].errors.map(err => err.message));
    } else {
      console.error("On Drop Rejected: ", fileRejections);
      setFileErrors([ "Could not upload file due to being rejected." ]);
    }

    setUploadingFile(null);
  }, []);

  const {
    getRootProps, getInputProps, isDragActive
  } = useDropzone({
    onDrop,
    accept: ".csv, application/vnd.ms-excel, text/csv",
    onDropRejected
  });

  const ImporterRestart = () => {
    return (
      <Button
        onClick={() => {
          setFileErrors(null);
          setImporting(null);
          setUploadingFile(null);
        }}
      >
        Start Again
      </Button>
    );
  };

  const handleImport = () => {
    if (key) {
      setImporting(true);
      const nutritionImportSetting = _.find(importSettings, setting => setting.type === "nutrition");

      const params: ImportFileParams = {
        s3Key: key,
        type: props.type
      };

      // Define the import setting to use if its a nutrition import
      if (props.type === "nutrition" && nutritionImportSetting) {
        params.importSettingId = nutritionImportSetting.id;
      }

      createImportJobRequest(params);
    }
  };

  return (
    <ModalWrapper
      isOpen={props.open}
      onBackgroundClick={() => props.onDismiss()}
    >
      <ModalInner>
        <Icon
          className="close-icon"
          icon={Icons.CLOSE}
          onClick={() => props.onDismiss()}
        />

        <Text
          className="modal-title"
          as="h2"
          fontSize="h2"
        >
          {`Import ${localTerminology.plural.text}`}
        </Text>

        {uploadingFile && (
          <SpinnerContainer>
            <LoadingSpinner
              type="circular"
              message="Uploading file"
              position="inline"
            />
          </SpinnerContainer>
        )}

        {importing && (
          <SpinnerContainer>
            <LoadingSpinner
              type="circular"
              message={`Importing ${localTerminology.plural.text}`}
              position="inline"
            />
          </SpinnerContainer>
        )}

        {fileErrors && (
          <RestartContainer>
            <ImportErrorContainer>
              {fileErrors.map((erroredStudentName, i) => (
                <ImporterError
                  key={i}
                  message={erroredStudentName}
                />
              ))}
            </ImportErrorContainer>

            <ButtonContainer>
              <ImporterRestart />
            </ButtonContainer>
          </RestartContainer>
        )}

        {uploadingFile === false && importing === null && !fileErrors && (
          <RestartContainer>
            <ImporterSuccess message="The file was successfully uploaded" />

            <Text as="h4">
              {`${localTerminology.plural.text} ready to import.`}
            </Text>

            <ButtonContainer>
              <Button onClick={() => handleImport()}>
                {`Import ${localTerminology.plural.text}`}
              </Button>
            </ButtonContainer>
          </RestartContainer>
        )}

        {uploadingFile === null && !fileErrors && (
          // @ts-ignore
          <DragAndDrogContainer
            {...getRootProps()}
            isDragActive={isDragActive}
          >

            <input {...getInputProps()} />

            <Icon
              icon={Icons.DOWNLOAD}
              $fillColor="primaryDark"
            />

            <Text as="h4">
              Drag 'n' drop your file or click here
            </Text>
          </DragAndDrogContainer>
        )}
      </ModalInner>
    </ModalWrapper>
  );
};

interface ResultProps {
  message: string;
}
export const ImporterError: React.ReactFCWithChildren<ResultProps> = ({ message }) => {
  return (
    <ResultContainer>
      <Text color="error">
        {message}
      </Text>
    </ResultContainer>
  );
};

export const ImporterSuccess: React.ReactFCWithChildren<ResultProps> = ({ message }) => {
  return (
    <ResultContainer>
      <Text>
        {message}
      </Text>
    </ResultContainer>
  );
};

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
`;

const ModalInner = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md};
    padding-top: 20px;
    position: relative;
    text-align: center;

    .close-icon {
      position: absolute;
      top: 5px;
      right: 5px;
    }
  `}
`;

const ModalWrapper = StyledModal.styled`
    position: relative;
    background: white;
    width: 500px;
    border-radius: 1rem;
    max-width: 100vw;
    min-height: 100px;
    opacity: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  button {
    margin-right: 10px;
  }
`;

const RestartContainer = styled.div``;
const ImportErrorContainer = styled.div``;

const DragAndDrogContainer = styled.div<{ isDragActive: boolean }>`
  ${({ theme, isDragActive }) => css`
    text-align: center;
    padding: 50px ${theme.spacing.padding.md};
    border: dashed 2px ${isDragActive ? theme.colors.primaryLight : theme.colors.primaryLight};
    border-radius: 4px;

    box-sizing: border-box;
  `}
`;

const ResultContainer = styled.div`
  display: block;
  height: ${({ theme }) => theme.fonts.sizes.h1};
  margin-top: ${({ theme }) => theme.spacing.padding.xs};
  span {
    vertical-align: middle;
    padding-left: ${({ theme }) => theme.spacing.padding.xs};
  }
`;
